import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Separator } from "@/components/ui/separator"
import { ScrollArea } from "@/components/ui/scroll-area"
import { 
  Copy, 
  Database, 
  Globe, 
  Users, 
  Building2, 
  Key, 
  Code, 
  ExternalLink,
  ChevronRight,
  CheckCircle
} from 'lucide-react'
import { toast } from 'sonner'

interface CodeBlockProps {
  code: string
  language?: string
  title?: string
}

function CodeBlock({ code, language = "bash", title }: CodeBlockProps) {
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(code)
      toast.success('Code copied to clipboard!')
    } catch (err) {
      toast.error('Failed to copy code')
    }
  }

  return (
    <div className="relative">
      {title && (
        <div className="bg-muted px-4 py-2 text-sm font-medium border rounded-t-lg border-b-0">
          {title}
        </div>
      )}
      <div className="bg-muted/30 p-4 rounded-lg border font-mono text-sm relative group">
        <Button
          size="sm"
          variant="ghost"
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
          onClick={copyToClipboard}
        >
          <Copy className="h-3 w-3" />
        </Button>
        <pre className="overflow-x-auto text-xs leading-relaxed pr-8">
          <code>{code}</code>
        </pre>
      </div>
    </div>
  )
}

interface EndpointCardProps {
  method: string
  endpoint: string
  description: string
  children: React.ReactNode
}

function EndpointCard({ method, endpoint, description, children }: EndpointCardProps) {
  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'bg-green-500/10 text-green-600 border-green-200'
      case 'POST': return 'bg-blue-500/10 text-blue-600 border-blue-200'
      case 'PUT': return 'bg-orange-500/10 text-orange-600 border-orange-200'
      case 'DELETE': return 'bg-red-500/10 text-red-600 border-red-200'
      default: return 'bg-gray-500/10 text-gray-600 border-gray-200'
    }
  }

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <Badge className={`font-mono text-xs px-2 py-1 ${getMethodColor(method)}`}>
            {method.toUpperCase()}
          </Badge>
          <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
            {endpoint}
          </code>
        </div>
        <p className="text-sm text-muted-foreground mt-2">{description}</p>
      </CardHeader>
      <CardContent>
        {children}
      </CardContent>
    </Card>
  )
}

export function ApiDocumentationTab() {
  const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''
  const BASE_URL = `${import.meta.env.VITE_SUPABASE_URL ?? ''}/functions/v1/data-api`

  const copyToClipboard = async (text: string, description: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${description} copied!`)
    } catch (err) {
      toast.error('Failed to copy to clipboard')
    }
  }

  const entities = [
    { name: 'accounts', icon: Building2, description: 'Company/account records', color: 'text-blue-600' },
    { name: 'contacts', icon: Users, description: 'Contact/person records', color: 'text-green-600' },
    { name: 'agencies', icon: Globe, description: 'Agency records', color: 'text-purple-600' },
    { name: 'app_directory', icon: Database, description: 'SaaS application records', color: 'text-orange-600' }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center py-6 border-b">
        <div className="flex items-center justify-center gap-3 mb-3">
          <Database className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Data API</h1>
        </div>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Unified HTTP interface for interacting with accounts, contacts, agencies, and SaaS applications. 
          Supports CRUD operations and domain-based searching.
        </p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
          <TabsTrigger value="examples">Examples</TabsTrigger>
          <TabsTrigger value="reference">Reference</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Quick Start */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Quick Start
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Base URL</h4>
                  <div className="flex items-center gap-2 bg-muted/50 p-3 rounded-lg">
                    <code className="text-sm flex-1 break-all">{BASE_URL}</code>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-8 w-8 p-0 flex-shrink-0" 
                      onClick={() => copyToClipboard(BASE_URL, 'Base URL')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2">Authentication</h4>
                  <div className="flex items-center gap-2 bg-muted/50 p-3 rounded-lg">
                    <code className="text-sm flex-1 break-all">Bearer {SUPABASE_ANON_KEY.substring(0, 20)}...</code>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="h-8 w-8 p-0 flex-shrink-0" 
                      onClick={() => copyToClipboard(`Bearer ${SUPABASE_ANON_KEY}`, 'Auth header')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Supported Entities */}
          <Card>
            <CardHeader>
              <CardTitle>Supported Entities</CardTitle>
              <p className="text-sm text-muted-foreground">
                The API supports operations on the following data entities:
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {entities.map((entity) => (
                  <div key={entity.name} className="flex items-center gap-3 p-3 border rounded-lg">
                    <entity.icon className={`h-5 w-5 ${entity.color}`} />
                    <div>
                      <code className="font-mono text-sm font-medium">{entity.name}</code>
                      <p className="text-xs text-muted-foreground">{entity.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Authentication Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Authentication
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                All requests require a valid Supabase API key in the Authorization header. 
                Use the buttons below to copy the required values:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button 
                  variant="outline" 
                  className="h-auto p-4 justify-start text-left"
                  onClick={() => copyToClipboard(`Bearer ${SUPABASE_ANON_KEY}`, 'Authorization header')}
                >
                  <div className="flex items-center gap-3 w-full">
                    <Copy className="h-4 w-4 flex-shrink-0" />
                    <div>
                      <div className="font-medium">Full Authorization Header</div>
                      <div className="text-xs text-muted-foreground">Ready to use in HTTP requests</div>
                    </div>
                  </div>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto p-4 justify-start text-left"
                  onClick={() => copyToClipboard(SUPABASE_ANON_KEY, 'API key')}
                >
                  <div className="flex items-center gap-3 w-full">
                    <Copy className="h-4 w-4 flex-shrink-0" />
                    <div>
                      <div className="font-medium">API Key Only</div>
                      <div className="text-xs text-muted-foreground">For custom implementations</div>
                    </div>
                  </div>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Endpoints Tab */}
        <TabsContent value="endpoints" className="space-y-6">
          <ScrollArea className="h-[600px] pr-4">
            {/* Search Records */}
            <EndpointCard
              method="GET"
              endpoint="/data-api/{entity}"
              description="Search for records with optional filters"
            >
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Query Parameters</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center py-1">
                      <code className="bg-muted px-2 py-1 rounded">domain</code>
                      <span className="text-muted-foreground">Filter by domain name</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <code className="bg-muted px-2 py-1 rounded">client_id</code>
                      <span className="text-muted-foreground">Filter by client ID</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <code className="bg-muted px-2 py-1 rounded">limit</code>
                      <span className="text-muted-foreground">Number of results (default: 50)</span>
                    </div>
                    <div className="flex justify-between items-center py-1">
                      <code className="bg-muted px-2 py-1 rounded">offset</code>
                      <span className="text-muted-foreground">Number of results to skip</span>
                    </div>
                  </div>
                </div>
                <CodeBlock
                  title="Example Request"
                  code={`curl "${BASE_URL}/accounts?domain=example.com" \\
  -H "Authorization: Bearer ${SUPABASE_ANON_KEY}"`}
                />
              </div>
            </EndpointCard>

            {/* Get Specific Record */}
            <EndpointCard
              method="GET"
              endpoint="/data-api/{entity}/{id}"
              description="Get a specific record by ID"
            >
              <CodeBlock
                title="Example Request"
                code={`curl "${BASE_URL}/accounts/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${SUPABASE_ANON_KEY}"`}
              />
            </EndpointCard>

            {/* Create Record */}
            <EndpointCard
              method="POST"
              endpoint="/data-api/{entity}"
              description="Create a new record"
            >
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Required Fields</h4>
                  <div className="space-y-1 text-sm">
                    <div><strong>Accounts:</strong> <code>company_name</code>, <code>client_id</code></div>
                    <div><strong>Contacts:</strong> <code>email</code>, <code>client_id</code></div>
                    <div><strong>Agencies:</strong> <code>name</code>, <code>client_id</code></div>
                  </div>
                </div>
                <CodeBlock
                  title="Example Request"
                  code={`curl -X POST "${BASE_URL}/accounts" \\
  -H "Authorization: Bearer ${SUPABASE_ANON_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "company_name": "Example Corp",
    "domain": "example.com",
    "client_id": 5559,
    "industry": "Technology"
  }'`}
                />
              </div>
            </EndpointCard>

            {/* Update Record */}
            <EndpointCard
              method="PUT"
              endpoint="/data-api/{entity}/{id}"
              description="Update an existing record by ID or domain"
            >
              <CodeBlock
                title="Example Request (by ID)"
                code={`curl -X PUT "${BASE_URL}/accounts/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${SUPABASE_ANON_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "industry": "Software",
    "priority_score": 95
  }'`}
              />
            </EndpointCard>

            {/* Delete Record */}
            <EndpointCard
              method="DELETE"
              endpoint="/data-api/{entity}/{id}"
              description="Delete a record by ID"
            >
              <CodeBlock
                title="Example Request"
                code={`curl -X DELETE "${BASE_URL}/accounts/123e4567-e89b-12d3-a456-426614174000" \\
  -H "Authorization: Bearer ${SUPABASE_ANON_KEY}"`}
              />
            </EndpointCard>
          </ScrollArea>
        </TabsContent>

        {/* Examples Tab */}
        <TabsContent value="examples" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Complete Workflow Example</CardTitle>
              <p className="text-sm text-muted-foreground">
                A typical workflow showing how to search, create, and update records
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">1</span>
                    Search for existing accounts
                  </h4>
                  <CodeBlock
                    code={`curl "${BASE_URL}/accounts?domain=shopify.com" \\
  -H "Authorization: Bearer ${SUPABASE_ANON_KEY}"`}
                  />
                </div>

                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">2</span>
                    Create new account if not found
                  </h4>
                  <CodeBlock
                    code={`curl -X POST "${BASE_URL}/accounts" \\
  -H "Authorization: Bearer ${SUPABASE_ANON_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "company_name": "Shopify Inc",
    "domain": "shopify.com",
    "client_id": 5559,
    "industry": "E-commerce Platform"
  }'`}
                  />
                </div>

                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <span className="bg-blue-100 text-blue-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">3</span>
                    Update with additional information
                  </h4>
                  <CodeBlock
                    code={`curl -X PUT "${BASE_URL}/accounts?domain=shopify.com" \\
  -H "Authorization: Bearer ${SUPABASE_ANON_KEY}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "priority_score": 90,
    "account_status": "Target Account"
  }'`}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Response Examples</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Successful Response</h4>
                  <CodeBlock
                    language="json"
                    code={`{
  "data": [
    {
      "id": "123e4567-e89b-12d3-a456-426614174000",
      "company_name": "Example Corp",
      "domain": "example.com",
      "client_id": 5559,
      "created_at": "2024-01-15T10:30:00Z"
    }
  ],
  "count": 1,
  "entity": "accounts",
  "filters": {
    "domain": "example.com",
    "limit": 50,
    "offset": 0
  }
}`}
                  />
                </div>

                <div>
                  <h4 className="font-medium mb-2">Error Response</h4>
                  <CodeBlock
                    language="json"
                    code={`{
  "error": "Invalid domain parameter",
  "details": "Domain must be a valid domain name"
}`}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reference Tab */}
        <TabsContent value="reference" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>HTTP Status Codes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><code>200</code> <span>Success</span></div>
                  <div className="flex justify-between"><code>201</code> <span>Created</span></div>
                  <div className="flex justify-between"><code>400</code> <span>Bad Request</span></div>
                  <div className="flex justify-between"><code>404</code> <span>Not Found</span></div>
                  <div className="flex justify-between"><code>405</code> <span>Method Not Allowed</span></div>
                  <div className="flex justify-between"><code>500</code> <span>Internal Server Error</span></div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Rate Limiting</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  The API inherits Supabase's built-in rate limiting. For high-volume usage, 
                  consider implementing client-side rate limiting to avoid hitting limits.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Domain-Based Search</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div><strong>Accounts:</strong> Exact match on <code>domain</code> field</div>
                  <div><strong>Contacts:</strong> Searches <code>email</code> and <code>company_email</code></div>
                  <div><strong>Agencies:</strong> Searches <code>website_url</code> and <code>contact_email</code></div>
                  <div><strong>App Directory:</strong> No domain-based search supported</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Integration Tips</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Always check HTTP status codes</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Validate required fields before requests</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Implement proper error handling</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Consider caching frequently accessed data</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Need Help?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  View the complete technical documentation for detailed field references and advanced usage.
                </p>
                <Button variant="outline" asChild>
                  <a href="/docs/data-api.md" target="_blank" className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4" />
                    Full Documentation
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}